import { __assign, __rest } from "tslib";
import * as React from 'react';
import clsx from 'clsx';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { default as MuiAvatar } from '@material-ui/core/Avatar';
import { silhouette } from './AvatarAssets';
import { Typography } from '../Typography';
// Map size descriptions to actual pixels.
var sizes = {
    'extra-small': 24,
    small: 32,
    medium: 42,
    large: 100,
    'extra-large': 160,
};
// How big the initials inside the avatar are in comparison to its diameter, and
// the smallest initials size -- this comes into play in the extra-small size.
// The smallest size is specified in pixels.
var initialsRatio = 0.33;
var minInitialsSize = 12;
/**
 * Determines the border color for the Avatar component based on its props and the theme.
 *
 * @param {AvatarProps} props - The properties of the Avatar component.
 * @param {Theme} theme - The theme object containing palette information.
 * @returns {string} - The color code for the border.
 */
var getBorderColor = function (props, theme) {
    if (props.selected) {
        return theme.palette.brand.main;
    }
    if (props.warning) {
        return theme.palette.error.main;
    }
    return theme.palette.border.main; // border gray
};
// Basic idea here is that the container specifies the overall size of the
// component, and everything inside it should position itself absolutely.
export var useStyles = makeStyles(function (theme) {
    return createStyles({
        container: function (props) { return ({
            display: 'inline-block',
            height: sizes[props.size] + theme.spacing(1),
            position: 'relative',
            width: sizes[props.size] + theme.spacing(1),
        }); },
        containerBordered: function (props) { return ({
            '&::before': {
                border: "2px solid ".concat(getBorderColor(props, theme)),
                borderRadius: '50%',
                bottom: 0,
                content: '""',
                left: 0,
                position: 'absolute',
                right: 0,
                top: 0,
            },
        }); },
        containerUnavailable: {
            '&::after': {
                // This background styling is done to avoid issues with color fringing
                // on rotated elements on Chrome, and to ensure correct appearance in
                // high-contrast mode.
                backgroundColor: theme.palette.background.default,
                backgroundImage: "linear-gradient(".concat(theme.palette.background.b5, ", ").concat(theme.palette.background.b5, ")"),
                borderBottom: "1px solid ".concat(theme.palette.background.default),
                borderTop: "1px solid ".concat(theme.palette.background.default),
                content: '""',
                height: '3px',
                left: 0,
                marginTop: '50%',
                position: 'absolute',
                top: 0,
                // translateY vertically centers the div.
                // rotate rotates it so that it's the correct orientation.
                // scaleX sets it to the length of the hypotenuse of the container -- we
                // don't have to worry about distortion becuase we're working with solid
                // colors.
                transform: "translateY(-50%) rotate(-45deg) scaleX(".concat(Math.sqrt(2), ")"),
                width: '100%',
                zIndex: 1,
            },
        },
        containerUnavailableThick: {
            '&::after': {
                borderBottom: "3px solid ".concat(theme.palette.background.default),
                borderTop: "3px solid ".concat(theme.palette.background.default),
                height: '9px',
            },
        },
    });
});
export var useMuiStyles = makeStyles(function () {
    return createStyles({
        // See also root styles in Avatar.overrides.ts.
        root: function (props) { return ({
            fontSize: Math.max(Math.ceil(sizes[props.size] * initialsRatio), minInitialsSize),
        }); },
    });
});
export var Avatar = React.forwardRef(function (props, ref) {
    var _a;
    var classes = useStyles(props);
    var muiClasses = useMuiStyles(props);
    var className = props.className, selected = props.selected, size = props.size, unavailable = props.unavailable, warning = props.warning, _b = props.variant, variant = _b === void 0 ? 'standard' : _b, muiProps = __rest(props, ["className", "selected", "size", "unavailable", "warning", "variant"]);
    var alt = props.alt, src = props.src, srcSet = props.srcSet, children = props.children;
    var muiAvatar;
    if (src || srcSet || children) {
        muiAvatar = (React.createElement(MuiAvatar, __assign({ classes: muiClasses, ref: ref }, muiProps),
            React.createElement("span", { "data-testid": "initial-container", "aria-hidden": children ? true : undefined }, children),
            children && React.createElement(Typography, { variant: "srOnly" }, alt)));
    }
    else {
        // Fall back to the generic silhouette.
        muiAvatar = React.createElement(MuiAvatar, __assign({ classes: muiClasses, ref: ref }, muiProps, { src: src !== null && src !== void 0 ? src : silhouette }));
    }
    return (React.createElement("div", { className: clsx(className, classes.container, (_a = {},
            _a[classes.containerBordered] = selected || warning || variant === 'outlined',
            _a[classes.containerUnavailable] = unavailable,
            _a[classes.containerUnavailableThick] = unavailable && size === 'extra-large',
            _a)) }, muiAvatar));
});
export default Avatar;
//# sourceMappingURL=Avatar.js.map